import React, { useEffect, useRef, useState, useCallback } from "react";
import { Camera, RefreshCw, ArrowLeft, Check, AlertTriangle, ImagePlus, RotateCw } from "lucide-react";

const VIEW_META = {
  front: { title: "FRONTALE", instruction: "Guarda direttamente la fotocamera." },
  left: { title: "LATO SINISTRO", instruction: "Ruota leggermente il viso verso destra, circa 30–45°." },
  right: { title: "LATO DESTRO", instruction: "Ruota leggermente il viso verso sinistra, circa 30–45°." },
};

export default function Capture({ views, messages = {}, existing = {}, onDone, onBack }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileRef = useRef(null);
  const startTokenRef = useRef(0); // guards against overlapping async starts
  const [idx, setIdx] = useState(0);
  const [captured, setCaptured] = useState({});
  const [preview, setPreview] = useState(null);
  const [camError, setCamError] = useState(false);
  const [ready, setReady] = useState(false); // video has a live, playing stream

  const view = views[idx];
  const meta = VIEW_META[view];

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    const v = videoRef.current;
    if (v) {
      try { v.pause(); } catch (e) { /* noop */ }
      v.srcObject = null;
    }
  }, []);

  // Acquire a FRESH MediaStream for the current step. Always release the
  // previous stream first, attach the new one, and only mark ready once the
  // video actually has dimensions (so the shutter is never enabled on a
  // stale/black frame).
  const startCamera = useCallback(async () => {
    const token = ++startTokenRef.current;
    setReady(false);
    setCamError(false);
    stopStream();

    if (!navigator.mediaDevices?.getUserMedia) {
      setCamError(true);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 1280 } },
        audio: false,
      });
      // A newer start was requested while we were awaiting -> discard this one.
      if (token !== startTokenRef.current) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      streamRef.current = stream;
      const v = videoRef.current;
      if (!v) return;
      v.srcObject = stream;

      const markReady = () => {
        if (token === startTokenRef.current && v.videoWidth > 0) setReady(true);
      };
      v.onloadedmetadata = async () => {
        try { await v.play(); } catch (e) { /* autoplay handled below */ }
        markReady();
      };
      v.oncanplay = markReady;
      try { await v.play(); } catch (e) { /* some browsers need the metadata event */ }
      markReady();
    } catch (e) {
      if (token === startTokenRef.current) setCamError(true);
    }
  }, [stopStream]);

  // (Re)start the camera whenever the capture step changes and no preview is
  // being reviewed. Cleanup releases the stream between steps and on unmount,
  // guaranteeing only one active stream at a time and a fresh one per angle.
  useEffect(() => {
    if (!preview) startCamera();
    return () => { stopStream(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx]);

  // Release camera fully on unmount.
  useEffect(() => () => { startTokenRef.current++; stopStream(); }, [stopStream]);

  const downscale = (canvas) => {
    const maxW = 1080;
    if (canvas.width <= maxW) return canvas.toDataURL("image/jpeg", 0.92);
    const scale = maxW / canvas.width;
    const c2 = document.createElement("canvas");
    c2.width = maxW;
    c2.height = Math.round(canvas.height * scale);
    c2.getContext("2d").drawImage(canvas, 0, 0, c2.width, c2.height);
    return c2.toDataURL("image/jpeg", 0.92);
  };

  const takePhoto = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const canvas = document.createElement("canvas");
    canvas.width = v.videoWidth;
    canvas.height = v.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1); // undo the on-screen mirror so the saved image is natural
    ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
    setPreview(downscale(canvas));
    // Keep the stream live while the user reviews the shot so "Rifai" is instant.
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        canvas.getContext("2d").drawImage(img, 0, 0);
        setPreview(downscale(canvas));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
    e.target.value = ""; // allow re-selecting the same file later
  };

  const confirmPhoto = () => {
    const next = { ...captured, [view]: preview };
    setCaptured(next);
    if (idx + 1 < views.length) {
      setPreview(null);
      setIdx(idx + 1); // triggers the effect -> fresh camera for the next angle
    } else {
      setPreview(null);
      startTokenRef.current++;
      stopStream();
      onDone(next);
    }
  };

  const redo = () => {
    setPreview(null);
    // If the stream was interrupted while reviewing, re-acquire it.
    if (!streamRef.current || !ready) startCamera();
  };

  const showVideoLayer = !preview;

  return (
    <div className="min-h-[100dvh] bg-slate-950 text-white flex flex-col">
      <div className="fs-container w-full pt-4 pb-3 flex items-center justify-between">
        <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-slate-300 fs-focus rounded" data-testid="capture-back-button">
          <ArrowLeft size={16} /> Indietro
        </button>
        <div className="flex gap-1.5">
          {views.map((v, i) => (
            <span
              key={v}
              className={`w-2.5 h-2.5 rounded-full transition-colors ${
                captured[v] ? "bg-[var(--fs-accent)]" : i === idx ? "bg-white" : "bg-slate-600"
              }`}
            />
          ))}
        </div>
      </div>

      <div className="fs-container w-full text-center mt-1">
        <p className="fs-eyebrow text-[var(--fs-accent)]" data-testid="capture-view-title">{meta.title}</p>
        <p className="text-sm text-slate-300 mt-1" data-testid="capture-instruction">{meta.instruction}</p>
        {messages[view]?.length > 0 && (
          <div className="mt-3 mx-auto max-w-sm rounded-xl bg-amber-500/15 border border-amber-400/40 p-3 text-left" data-testid="capture-retake-message">
            <div className="flex items-center gap-2 text-amber-300 text-xs font-semibold mb-1">
              <AlertTriangle size={14} /> Da rifare
            </div>
            {messages[view].map((m, i) => (
              <p key={i} className="text-xs text-amber-100 leading-snug">{m}</p>
            ))}
          </div>
        )}
      </div>

      <div className="flex-1 flex items-center justify-center px-4 py-5">
        <div className="relative w-full max-w-sm aspect-[3/4] rounded-3xl overflow-hidden bg-slate-900 border border-slate-700">
          {/* Video is ALWAYS mounted so the live stream stays attached across steps. */}
          <video
            ref={videoRef}
            playsInline
            autoPlay
            muted
            className={`w-full h-full object-cover ${showVideoLayer && !camError ? "" : "invisible"}`}
            style={{ transform: "scaleX(-1)" }}
            data-testid="capture-video"
          />

          {preview && (
            <img src={preview} alt="Anteprima" className="absolute inset-0 w-full h-full object-cover" data-testid="capture-preview" />
          )}

          {showVideoLayer && camError && (
            <div className="absolute inset-0 grid place-items-center p-6 text-center bg-slate-900" data-testid="capture-cam-error">
              <div>
                <Camera size={32} className="mx-auto text-slate-500" />
                <p className="text-sm text-slate-300 mt-3">Fotocamera non disponibile.</p>
                <p className="text-xs text-slate-500 mt-1">Riapri la fotocamera oppure carica una fotografia dal dispositivo.</p>
                <button onClick={startCamera} className="mt-4 px-5 py-2.5 rounded-full bg-white text-slate-900 text-sm font-semibold inline-flex items-center gap-2 fs-focus" data-testid="capture-reopen-button">
                  <RotateCw size={16} /> RIAPRI FOTOCAMERA
                </button>
              </div>
            </div>
          )}

          {showVideoLayer && !camError && !ready && (
            <div className="absolute inset-0 grid place-items-center bg-slate-900/70" data-testid="capture-activating">
              <div className="text-center">
                <span className="w-8 h-8 rounded-full border-2 border-white/70 border-t-transparent animate-spin inline-block" />
                <p className="text-xs text-slate-300 mt-3">Attivazione fotocamera…</p>
              </div>
            </div>
          )}

          {showVideoLayer && !camError && (
            <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 133" preserveAspectRatio="none">
              <ellipse cx="50" cy="60" rx="32" ry="42" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="0.6" strokeDasharray="3 2.5" />
            </svg>
          )}
        </div>
      </div>

      <div className="fs-container w-full pb-8">
        <input ref={fileRef} type="file" accept="image/*" capture="user" onChange={onFile} className="hidden" data-testid="capture-file-input" />
        {preview ? (
          <div className="flex gap-3">
            <button onClick={redo} className="fs-btn-ghost flex-1 py-4 flex items-center justify-center gap-2 fs-focus" data-testid="capture-redo-button">
              <RefreshCw size={18} /> Rifai
            </button>
            <button onClick={confirmPhoto} className="fs-btn-primary flex-1 py-4 flex items-center justify-center gap-2 fs-focus" data-testid="capture-confirm-button">
              <Check size={18} /> {idx + 1 < views.length ? "Avanti" : "Fine"}
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-center gap-4">
            <button onClick={() => fileRef.current?.click()} className="w-12 h-12 grid place-items-center rounded-full bg-slate-800 border border-slate-700 fs-focus" data-testid="capture-upload-button" aria-label="Carica foto">
              <ImagePlus size={20} className="text-slate-200" />
            </button>
            <button
              onClick={takePhoto}
              disabled={camError || !ready}
              className="w-20 h-20 rounded-full bg-white grid place-items-center disabled:opacity-40 fs-focus active:scale-95 transition-transform"
              data-testid="capture-shutter-button"
              aria-label="Scatta"
            >
              <span className="w-16 h-16 rounded-full border-4 border-[var(--fs-blue)]" />
            </button>
            {camError ? (
              <button onClick={startCamera} className="w-12 h-12 grid place-items-center rounded-full bg-slate-800 border border-slate-700 fs-focus" data-testid="capture-reopen-small-button" aria-label="Riapri fotocamera">
                <RotateCw size={18} className="text-slate-200" />
              </button>
            ) : (
              <span className="w-12" />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
